package nl.volleyplanner.web.controller.planner

import kotlinx.browser.document
import kotlinx.coroutines.async
import kotlinx.coroutines.flow.*
import kotlinx.coroutines.launch
import kotlinx.html.*
import kotlinx.html.dom.create
import kotlinx.html.js.div
import kotlinx.html.js.onClickFunction
import nl.volleyplanner.api.GroupType
import nl.volleyplanner.api.auth.AuthLevel
import nl.volleyplanner.api.planner.PersonTaskStatistics
import nl.volleyplanner.api.planner.ProblemStatistics
import nl.volleyplanner.api.planner.TaskAssignmentConstraintRuleType
import nl.volleyplanner.api.types.BigNumber
import nl.volleyplanner.sportconfig.NevoboAgeGroup
import nl.volleyplanner.sportconfig.NevoboTeamName
import nl.volleyplanner.types.AgeGroup
import nl.volleyplanner.types.Gender
import nl.volleyplanner.web.CoroutineManager
import nl.volleyplanner.web.Logger
import nl.volleyplanner.web.VolleyPlannerApplication
import nl.volleyplanner.web.base.*
import nl.volleyplanner.web.base.component.badge
import nl.volleyplanner.web.base.routing.PageMountContext
import nl.volleyplanner.web.base.routing.PageRouteContext
import nl.volleyplanner.web.base.routing.RouteChange
import nl.volleyplanner.web.component.PersonScheduleModal
import nl.volleyplanner.web.component.PersonStatisticsScoreBadge
import nl.volleyplanner.web.launchAsync
import nl.volleyplanner.web.lib.Tab
import nl.volleyplanner.web.lib.Tooltip
import nl.volleyplanner.web.util.BootstrapColor
import org.w3c.dom.HTMLElement
import org.w3c.dom.get

data class ProblemStatisticsParameters(
    val problemSlug: String,
    val problemVersionSlug: String?
)

class TaskAssignmentStatisticsController(override val parent: ProblemEditorController) : WebPageController() {
    override val name = "TaskAssignmentStatisticsController"
    override val path = "/statistieken"
    override val requiredAuthLevel = AuthLevel.AdminUser
    override val childPages: List<WebPageController> = emptyList()

    private val parametersFlow = MutableStateFlow<ProblemStatisticsParameters?>(null)
    private val statisticsState = MutableStateFlow<ProblemStatistics?>(null)
    private val filterState = MutableStateFlow<Set<String>>(emptySet())

    operator fun Int?.plus(other: Int?) = (this ?: 0) + (other ?: 0)
    operator fun BigNumber?.plus(other: BigNumber?) = (this ?: BigNumber.ZERO) + (other ?: BigNumber.ZERO)

    override fun createRuntimeInstance() = TaskAssignmentStatisticsController(parent)

    override suspend fun mount(context: PageMountContext) {
        super.mount(context)

        parametersFlow
            .onEach { repaint() }
            .launchIn(CoroutineManager.RENDER_SCOPE)

        val problemSlug = context.pageRouteContext.pathParameters.getValue("problem_slug")
        val versionSlug = context.pageRouteContext.urlState.queryParameters.get("version")

        parametersFlow.value = ProblemStatisticsParameters(problemSlug, versionSlug)
    }

    override suspend fun update(change: RouteChange) {
        super.update(change)
        val problemSlug = change.current.pathParameters.getValue("problem_slug")
        val versionSlug = change.current.urlState.queryParameters.get("version")
        parametersFlow.value = ProblemStatisticsParameters(problemSlug, versionSlug)
    }

    override suspend fun loadPageMeta(pageContext: PageRouteContext): PageMeta {
        return PageMeta("Programma statistieken", "/statistieken")
    }

    inner class StatisticsComponent(val filterComponent: FilterComponent, val forTeam: Boolean = false) :
        HTMLComponent() {

        override fun onContentReplaced(htmlElement: HTMLElement) {
            super.onContentReplaced(htmlElement)
            val tooltipElements = document.querySelectorAll("[data-bs-toggle=\"tooltip\"]")
            for (i in 0 until tooltipElements.length) {
                val tooltipElement = tooltipElements[i] as HTMLElement
                Tooltip.getOrCreateInstance(tooltipElement)
            }
        }

        override fun render(consumer: TagConsumer<HTMLElement>): HTMLElement {
            val personScheduleModal = PersonScheduleModal()
            val personScheduleModalComponent = componentScope.async {
                createStandaloneComponent(personScheduleModal)
            }
            return consumer.div(classes = "container-xl") {
                div(classes ="sticky-table vh-80") {
                    table(classes = "table table-striped table-hover") {
                        thead {
                            tr {
                                if (!forTeam) {
                                    th(scope = ThScope.col) {
                                        +"Naam"
                                    }
                                }
                                th(scope = ThScope.col) { +"Teams" }
                                if (forTeam) th(scope = ThScope.col) { +"# Spelers" }
                                th(scope = ThScope.col) { +"# Taken" }
                                th(scope = ThScope.col) { +"# Wedstr" }
                                th(scope = ThScope.col) { +"Tot Score" }
//                                th(scope = ThScope.col, classes = "d-none d-xxl-table-cell") { +"Gem Ass Score" }
                                th(scope = ThScope.col) { +"Vrijw. score" }
//                                th(scope = ThScope.col) { +"ET Uncap-ped" }
                                //if (!forTeam) th(scope = ThScope.col, classes = "d-none d-xxl-table-cell") { +"Verschil" }
                            }
                        }
                        tbody(classes = "table-group-divider") {
                            if (statisticsState.value == null) {
                                tr { td { spinner(SpinnerStyle.Border) } }
                            } else {
                                val records = if (!forTeam) {
                                    statisticsState.value!!.personStatistics
                                } else {
                                    statisticsState.value!!.personStatistics
                                        .groupBy {
                                            it.person.groups?.get(GroupType.Team.paramValue)
                                                ?.first()
                                        }.map { (team, records) ->
                                            records.reduce { acc, record ->
                                                record.copy(
                                                    personImpacts = acc.personImpacts + record.personImpacts,
                                                    tasksById = acc.tasksById + record.tasksById,
                                                    volunteerScore = acc.volunteerScore + record.volunteerScore
                                                )
                                            }
                                        }
                                }
                                val acceptedRuleTypes = setOf(TaskAssignmentConstraintRuleType.Helper, TaskAssignmentConstraintRuleType.PersonImpact)

                                records
                                    .filter { filterComponent.showRecord(it) }
                                    .sortedWith(compareBy(
                                        { it.totalScore }
                                    ))
                                    .forEach { personStatistics ->
                                        tr {
                                            if (!forTeam) td {
                                                div {
                                                    a {
                                                        href = "#"
                                                        onClickFunction = { event ->
                                                            event.preventDefault()
                                                            componentScope.launch {
                                                                personScheduleModalComponent.await().let {
                                                                    it.setPerson(
                                                                        personStatistics.person,
                                                                        statisticsState.value!!.problem.startDate,
                                                                        statisticsState.value!!.problem.endDate,
                                                                        parametersFlow.value!!.problemVersionSlug,
                                                                        availableTaskStatistics = statisticsState.value!!.taskStatisticsById
                                                                            .filter { personStatistics.tasksById.contains(it.key) }
                                                                    )
                                                                    it.show()
                                                                }
                                                            }
                                                        }
                                                        +personStatistics.person.name
                                                    }

                                                    personStatistics.person.groups
                                                        ?.get(GroupType.Scheidsrechters.paramValue)
                                                        ?.first()
                                                        ?.let {
                                                            badge(color = BootstrapColor.Primary, small = true) {
                                                                classes += "ms-2"
                                                                +it.name
                                                            }
                                                        }
                                                }
                                                if (personStatistics.person.volunteerJobs?.isNotEmpty() ?: false) {
                                                    div {
                                                        style = "max-width: 400px;"
                                                        personStatistics.person.volunteerJobs!!.forEach { volunteerJob ->
                                                            badge(color = BootstrapColor.Warning, small = true) {
                                                                classes += "me-1"
                                                                +volunteerJob.jobName
                                                            }
                                                        }
                                                    }
                                                }

                                            }
                                            td {
                                                +"${
                                                    personStatistics.person.groups
                                                        ?.get(GroupType.Team.paramValue)
                                                        ?.joinToString(", ") { NevoboTeamName.parse(it.name).teamIdentifier }
                                                }"
                                            }
                                            if (forTeam) td {
                                                +"${statisticsState.value!!.personStatistics.count { 
                                                    it.person.groups?.get(GroupType.Team.paramValue)?.first() == personStatistics.person.groups?.get(GroupType.Team.paramValue)?.first() 
                                                }}"
                                                //+"${statisticsState.value!!.records.count { it.team == personStatistics.team }}"
                                            }
                                            td { +"${personStatistics.tasksById.size}" }
                                            td { span { +"${personStatistics.matchAmount}" } }
                                            td {
                                                simpleComponent(PersonStatisticsScoreBadge(personStatistics, statisticsState.value!!.taskStatisticsById))
                                            }
                                            td { +"${personStatistics.volunteerScore}" }
                                        }
                                    }
                            }
                        }
                    }
                }
            }
        }

        override suspend fun getState(): StateFlow<*> = statisticsState
            .combine(filterState, { a, b -> Pair(a, b) })
            .stateIn(
                CoroutineManager.RENDER_SCOPE,
                SharingStarted.Eagerly,
                Pair(statisticsState.value, filterState.value)
            )
    }

    override fun onReady() {
        super.onReady()
        val triggerList = document.querySelectorAll("#statisticsTab button")
        for (i in 0 until triggerList.length) {
            val triggerElement = triggerList[i] as HTMLElement
            val bootstrapTabTrigger = Tab(triggerElement)
            triggerElement.addEventListener("click", { event ->
                event.preventDefault()
                bootstrapTabTrigger.show()
            })
        }
    }


    inner class FilterComponent() : HTMLComponent() {
        private val logger =
            Logger.getLogger("nl.volleyplanner.page.TaskAssignmentStatisticsController.FilterComponent")

        fun showRecord(record: PersonTaskStatistics): Boolean {
            val active = filterState.value
            if (active.isEmpty()) return true
            val personTeams = record.person.groups?.get("Team")?.map { it.name } ?: emptyList()
            return personTeams.any { it in active }
        }

        override fun render(consumer: TagConsumer<HTMLElement>): HTMLElement {
            filterState.value = emptySet()
            return consumer.div(classes = "container") {
                if (statisticsState.value == null) {
                    spinner(SpinnerStyle.Border)
                } else {
                    val teams = statisticsState.value!!.personStatistics
                        .mapNotNull { it.person.groups?.get("Team")?.map { g -> g.name } }
                        .flatten()
                        .distinct()
                    val teamsNevoboNames = teams.map { NevoboTeamName.parse(it) }

                    fun createFilterGroup(groupLabel: String, gender: Gender, ageGroups: Set<AgeGroup>): nl.volleyplanner.web.base.component.ToggleButtonGroup<String> {
                        val items = teamsNevoboNames
                            .filter { team -> team.gender == gender && ageGroups.contains(team.ageGroup) }
                            .map { team ->
                                nl.volleyplanner.web.base.component.ToggleButtonGroup.ToggleButton(
                                    label = team.teamIdentifier,
                                    key = team.fullTeamName,
                                    defaultState = true
                                )
                            }
                            .sortedBy { it.label }
                        return nl.volleyplanner.web.base.component.ToggleButtonGroup(groupLabel, filterState, items)
                    }

                    div(classes = "row btn-toolbar") {
                        role = "toolbar"
                        attributes["aria-label"] = "Heren & Jongens team filters"

                        div(classes = "col") {
                            component(createFilterGroup("Heren", Gender.Men, setOf(NevoboAgeGroup.Senior)))
                        }
                        div(classes = "col") {
                            component(createFilterGroup("Jongens", Gender.Men, setOf(NevoboAgeGroup.A, NevoboAgeGroup.B, NevoboAgeGroup.C)))
                        }
                    }

                    div(classes = "row btn-toolbar") {
                        role = "toolbar"
                        attributes["aria-label"] = "Dames & meisjes team filters"

                        div(classes = "col") {
                            component(createFilterGroup("Dames", Gender.Women, setOf(NevoboAgeGroup.Senior)))
                        }

                        div(classes = "col") {
                            component(createFilterGroup("Meisjes", Gender.Women, setOf(NevoboAgeGroup.A, NevoboAgeGroup.B, NevoboAgeGroup.C)))
                        }
                    }

                    div(classes = "row btn-toolbar") {
                        role = "toolbar"
                        attributes["aria-label"] = "CMV team filters"

                        div(classes = "col") {
                            component(createFilterGroup("CMV", Gender.Mixed, setOf(NevoboAgeGroup.CMV)))
                        }
                    }
                }
            }
        }

        override suspend fun getState() = statisticsState
    }

    override suspend fun renderContent(): HTMLElement {
        // TODO: Problem selection
        launchAsync {
            statisticsState.value = null
            VolleyPlannerApplication.volleyplannerApiPlannerClient.getTaskAssignmentStatisticsV2(
                parametersFlow.value!!.problemSlug, parametersFlow.value!!.problemVersionSlug
            )
        }.onComplete {
                statisticsState.value = it
                filterState.value = emptySet()
            }

        val mainElement = document.create.div {
            h1 { +"Thuisprogramma Statistieken" }
            val filterComponent = FilterComponent()
            component(filterComponent)
            ul(classes = "nav nav-tabs mt-3") {
                id = "statisticsTab"
                role = "tablist"

                li(classes = "nav-item") {
                    role = "presentation"
                    button(classes = "nav-link active") {
                        id = "personstatistics-tab"
                        attributes["data-bs-toggle"] = "tab"
                        attributes["data-bs-target"] = "#person-statistics-pane"
                        type = ButtonType.button
                        role = "tab"
                        attributes["aria-controls"] = "person-statistics-pane"
                        attributes["aria-selected"] = "true"
                        +"Persoon statistieken"
                    }
                }
                li(classes = "nav-item") {
                    role = "presentation"
                    button(classes = "nav-link") {
                        id = "personstatistics-tab"
                        attributes["data-bs-toggle"] = "tab"
                        attributes["data-bs-target"] = "#team-statistics-pane"
                        type = ButtonType.button
                        role = "tab"
                        attributes["aria-controls"] = "team-statistics-pane"
                        attributes["aria-selected"] = "false"
                        +"Team statistieken"
                    }
                }
            }
            div(classes = "tab-content") {
                id = "statistics-tab-content"

                div(classes = "tab-pane fade show active") {
                    id = "person-statistics-pane"
                    role = "tabpanel"
                    attributes["aria-labelledby"] = "personstatistics-tab"

                    component(StatisticsComponent(filterComponent))
                }
                div(classes = "tab-pane fade") {
                    id = "team-statistics-pane"
                    role = "tabpanel"
                    attributes["aria-labelledby"] = "teamstatistics-tab"

                    component(StatisticsComponent(filterComponent, true))
                }
            }
        }

        return mainElement
    }
}